import { Calendar, Trophy } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/Layout'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { buildEasternIsoString } from '../../lib/dateUtils'
import {
  createTournament as createTournamentService,
  fetchSports as fetchSportsService,
} from '../../services/tournamentService'

export default function TournamentCreate() {
  const navigate = useNavigate()
  const sportTriggerRef = useRef(null)
  const [sports, setSports] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  const [formData, setFormData] = useState({
    sportId: '',
    name: '',
    startDate: '',
    endDate: '',
    submissionDeadline: '',
    submissionDeadlineTime: '23:59',
    isTest: false,
  })

  // Fetch sports on component mount
  useEffect(() => {
    fetchSports()
  }, [])

  const fetchSports = async () => {
    try {
      const data = await fetchSportsService()
      setSports(data || [])
      // Set first sport as default
      if (data && data.length > 0) {
        setFormData(prev => ({ ...prev, sportId: data[0].id }))
        // Focus the sport dropdown after data loads
        requestAnimationFrame(() => sportTriggerRef.current?.focus())
      }
    } catch (err) {
      console.error('Error fetching sports:', err)
      setError('Failed to load sports')
    }
  }

  const handleInputChange = (field, value) => {
    // If start date is being changed, auto-populate submission deadline
    if (field === 'startDate' && value) {
      const startDate = new Date(value)
      const dayBefore = new Date(startDate)
      dayBefore.setDate(dayBefore.getDate() - 1)
      const submissionDeadline = dayBefore.toISOString().split('T')[0]
      
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        submissionDeadline: submissionDeadline
      }))
    } else {
      setFormData(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleCreateTournament = async () => {
    // Validation
    if (!formData.name || !formData.startDate || !formData.endDate || !formData.sportId) {
      setError('Please fill in all required fields')
      return
    }

    setLoading(true)
    setError('')

    try {
      // Combine submission deadline date and time in Eastern Time with fixed offset
      const submissionDeadline = formData.submissionDeadline && formData.submissionDeadlineTime
        ? buildEasternIsoString(formData.submissionDeadline, formData.submissionDeadlineTime)
        : null

      const data = await createTournamentService({
        name: formData.name,
        sportId: formData.sportId,
        startDate: `${formData.startDate}T00:00:00`,
        endDate: `${formData.endDate}T23:59:59`,
        submissionDeadline,
        isTest: formData.isTest,
      })

      // Admin creates tournaments from inside the Admin Dashboard, so keep
      // them in admin land — drop straight into the edit page with the Add
      // Bracket CTA as their next step.
      navigate(`/admin/tournaments/${data.id}/edit`, { state: { justCreated: true } })
    } catch (err) {
      console.error('Error creating tournament:', err)
      setError(err.message || 'Failed to create tournament. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Create Tournament</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Tournament Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}

            {/* Sport Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Sport *</label>
              <Select value={formData.sportId} onValueChange={(value) => handleInputChange('sportId', value)} disabled={sports.length === 0}>
                <SelectTrigger ref={sportTriggerRef} className="w-full">
                  <SelectValue placeholder="Select a sport..." />
                </SelectTrigger>
                <SelectContent>
                  {sports.map((sport) => (
                    <SelectItem key={sport.id} value={sport.id}>
                      {sport.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tournament Name */}
            <div>
              <label className="block text-sm font-medium mb-2">Tournament Name *</label>
              <Input
                placeholder="e.g., Spring Championship 2025"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Start Date *
                </label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  End Date *
                </label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                />
              </div>
            </div>

            {/* Submission Deadline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Submission Deadline Date</label>
                <Input
                  type="date"
                  value={formData.submissionDeadline}
                  onChange={(e) => handleInputChange('submissionDeadline', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Submission Deadline Time (ET)</label>
                <Input
                  type="time"
                  value={formData.submissionDeadlineTime}
                  onChange={(e) => handleInputChange('submissionDeadlineTime', e.target.value)}
                />
              </div>
            </div>

            {/* Test mode */}
            <div className="flex items-center gap-2 rounded-md border border-dashed p-3 bg-muted/30">
              <input
                id="tournament-create-is-test"
                type="checkbox"
                checked={formData.isTest}
                onChange={(e) => handleInputChange('isTest', e.target.checked)}
                className="h-4 w-4"
              />
              <label htmlFor="tournament-create-is-test" className="text-sm font-medium cursor-pointer">
                Test mode
              </label>
            </div>

            {/* Create Button */}
            <div className="flex justify-end pt-4">
              <Button
                variant="primaryNeon"
                onClick={handleCreateTournament}
                size="lg"
                disabled={loading || !formData.name || !formData.startDate || !formData.endDate || !formData.sportId}
              >
                <Trophy className="h-4 w-4 mr-2" />
                {loading ? 'Creating...' : 'Create Tournament'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
